import { useAuthStore } from "@/stores/use-auth-store";
const APIBASE = import.meta.env.VITE_API_URL;

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export async function GET(url: string, retry: boolean = true) {
  const { accessToken, setAccessToken, logout } = useAuthStore.getState();

  let response = await fetch(`${APIBASE}${url}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401 && retry) {
    const refreshResponse = await refreshToken();

    if (refreshResponse.ok) {
      const data = await refreshResponse.json();
      if (data.accessToken) {
        setAccessToken(data.accessToken);
        return await GET(url, false);
      }
    } else {
      logout();
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || `API call failed with status ${response.status} for ${url}`,
      response.status,
      errorData.code
    );
  }
  const data = await response.json();
  return data;
}

export async function GETBlob(url: string, retry: boolean = true) {
  const { accessToken, setAccessToken, logout } = useAuthStore.getState();

  let response = await fetch(`${APIBASE}${url}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401 && retry) {
    const refreshResponse = await refreshToken();

    if (refreshResponse.ok) {
      const data = await refreshResponse.json();
      if (data.accessToken) {
        setAccessToken(data.accessToken);
        return await GETBlob(url, false);
      }
    } else {
      logout();
    }
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Server returned ${response.status}: ${errorText}`);
  }

  const blob = await response.blob();
  return blob;
}

export async function GET_PUBLIC(url: string) {
  const response = await fetch(`${APIBASE}${url}`, {
    method: "GET"
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || `API call failed with status ${response.status} for ${url}`,
      response.status,
      errorData.code
    );
  }

  return response.json();
}

export async function POST_PUBLIC(url: string, body: any) {
  const response = await fetch(`${APIBASE}${url}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || `API call failed with status ${response.status} for ${url}`,
      response.status,
      errorData.code
    );
  }

  return response.json();
}

export async function POST(url: string, body: any, retry: boolean = true) {
  const { accessToken, setAccessToken, logout } = useAuthStore.getState();

  let response = await fetch(`${APIBASE}${url}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401 && retry) {
    const refreshResponse = await refreshToken();

    if (refreshResponse.ok) {
      const data = await refreshResponse.json();
      if (data.accessToken) {
        setAccessToken(data.accessToken);
        return await POST(url, body, false);
      }
    } else {
      logout();
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || `API call failed with status ${response.status} for ${url}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data;
}

export async function PUT(url: string, body: any, retry: boolean = true) {
  const { accessToken, setAccessToken, logout } = useAuthStore.getState();


  const isFormData = body instanceof FormData;

  const headers: HeadersInit = {
    Authorization: `Bearer ${accessToken}`,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  let response = await fetch(`${APIBASE}${url}`, {
    method: "PUT",
    headers,
    body: isFormData ? body : JSON.stringify(body),
  });

  if (response.status === 401 && retry) {
    const refreshResponse = await refreshToken();

    if (refreshResponse.ok) {
      const data = await refreshResponse.json();
      if (data.accessToken) {
        setAccessToken(data.accessToken);
        return await PUT(url, body, false);
      }
    } else {
      logout();
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || `API call failed with status ${response.status} for ${url}`;
    throw new Error(errorMessage);
  }
  const data = await response.json();
  return data;
}

export async function DELETE(url: string, retry: boolean = true) {
  const { accessToken, setAccessToken, logout } = useAuthStore.getState();

  let response = await fetch(`${APIBASE}${url}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 401 && retry) {
    const refreshResponse = await refreshToken();

    if (refreshResponse.ok) {
      const data = await refreshResponse.json();
      if (data.accessToken) {
        setAccessToken(data.accessToken);
        return await DELETE(url, false);
      }
    } else {
      logout();
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || `API call failed with status ${response.status} for ${url}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data;
}

export async function refreshToken() {
  const refreshResponse = await fetch(`${APIBASE}/api/auth/refresh`, {
    method: "POST",
    credentials: "include",
  });

  return refreshResponse;
}
